import React from 'react';
import ReactModal from 'react-modal';
import closeSvg from 'src/assets/close.svg';
import { AlertAdapter, DatePicker, RegularButton } from 'pg-ui-library';
import NewHireExceptionService from 'src/services/new-hire-exception.service';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { INewHireException } from 'src/interfaces';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import {
  NewHireExceptionModalHeader,
  NewHireExceptionModalFooter,
  NewHireExceptionModalHeaderTitle,
  NewHireExceptionModalBody,
  NewHireExceptionModalLabel,
  NewHireExceptionModalNumber,
  NewHireExceptionModalForm,
  NewHireExceptionModalFormNumber,
} from './styles';
import { CloseIcon } from '../reset-password-modal/styles';

export const NewHireExceptionModal: React.FC<{
  newHireException: string[];
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  setNewHireExceptionModal: (b: boolean) => void;
}> = ({ newHireException, isOpen, setNewHireExceptionModal }) => {
  const { t } = useTranslation();
  const today = new Date();
  const maxDate = new Date();
  maxDate.setMonth(today.getMonth() + 3);

  const handleModal = () => {
    setNewHireExceptionModal(!isOpen);
  };

  const handleModalClose = () => {
    setNewHireExceptionModal(false);
  };

  const newHireExceptionSchema = yup.object({
    advance: yup.date().min(today).max(maxDate).nullable(),
    extend: yup.number().min(0).max(60).nullable(),
  });

  const mutationCreate = useMutation(
    (variables: INewHireException) =>
      NewHireExceptionService.create({
        employeeId: newHireException,
        advance: variables.advance,
        extend: variables.extend,
      }),
    {
      onSuccess: () => {
        AlertAdapter('New Hire Exception created successfully!', 'success');
        setNewHireExceptionModal(false);
      },
      onError: () => {
        AlertAdapter('Failed to create New Hire Exception', 'error');
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      advance: undefined,
      extend: 0,
    },
    validationSchema: newHireExceptionSchema,
    onSubmit: async (values: INewHireException) => {
      mutationCreate.mutate(values);
    },
  });

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '10px',
          margin: 'auto',
          width: '460px',
          height: 'max-content',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: '#E4E4EB',
        },
      }}
      onRequestClose={handleModalClose}
    >
      <form onSubmit={formik.handleSubmit}>
        <NewHireExceptionModalHeader>
          <NewHireExceptionModalHeaderTitle>{t('newhireException.modal.title')}</NewHireExceptionModalHeaderTitle>
          <CloseIcon style={{ marginBottom: '7%', marginRight: '1%' }} src={closeSvg} onClick={() => handleModal()} />
        </NewHireExceptionModalHeader>
        <NewHireExceptionModalBody>
          <NewHireExceptionModalForm>
            <div style={{ marginRight: '5%', marginTop: '5%' }}>
              <DatePicker
                id="advance"
                name="advance"
                selected={formik.values.advance}
                onChange={date => formik.setFieldValue('advance', date)}
                label={t('newhireException.modal.extend')}
                placeholderText="MM/DD/YY"
                minDate={today}
                maxDate={maxDate}
              />
            </div>
          </NewHireExceptionModalForm>
          <NewHireExceptionModalForm>
            <NewHireExceptionModalLabel>{t('newhireException.modal.advance')}</NewHireExceptionModalLabel>
            <NewHireExceptionModalFormNumber style={{ color: 'black' }}>
              <NewHireExceptionModalNumber
                id="extend"
                name="extend"
                onChange={formik.handleChange}
                min={0}
                placeholder="Type Here..."
                type="number"
              />
              <NewHireExceptionModalLabel>{t('newhireException.modal.advanceDays')}</NewHireExceptionModalLabel>
            </NewHireExceptionModalFormNumber>
          </NewHireExceptionModalForm>
        </NewHireExceptionModalBody>
        <NewHireExceptionModalFooter>
          <RegularButton type="submit" disabled={!formik.isValid || mutationCreate.isLoading} style={{ width: '44%' }}>
            {t('newhireException.modal.button')}
          </RegularButton>
        </NewHireExceptionModalFooter>
      </form>
    </ReactModal>
  );
};
